<!--
  The standard app shell: top toolbar, SideNav, one scrolling `.app-content`.
  This screen used to render a bare `mat-card` with no shell at all, which
  made it the only operator screen you could not navigate away from without
  the browser's back button.
-->
<div class="app-container" [class.is-mobile]="mobileQuery.matches">
  <mat-toolbar color="primary" class="app-toolbar">
    <div class="app-toolbar-sections">
      <!-- Start: Hamburger + Title -->
      <div class="app-toolbar-start">
        <button mat-icon-button (click)="mainSideNav.toggle()"><mat-icon>menu</mat-icon></button>
        <span class="app-toolbar-title" i18n="@@Project Mapping">Project Mapping</span>
      </div>

      <!-- End -->
      <div class="app-toolbar-end">
        <app-shopping-cart-top-bar-button></app-shopping-cart-top-bar-button>
        <app-active-role></app-active-role>
      </div>
    </div>
  </mat-toolbar>
  <mat-sidenav-container class="app-sidenav-container" (backdropClick)="mainSideNav.close()" [style.marginTop.px]="mobileQuery.matches ? 56 : 0">
    <mat-sidenav opened #mainSideNav [mode]="mobileQuery.matches ? 'over' : 'side'" (keydown.escape)="mainSideNav.close()" [fixedInViewport]="mobileQuery.matches" fixedTopGap="56">
      <mat-nav-list>
        <app-side-nav focus="projectmapping"></app-side-nav>
      </mat-nav-list>
    </mat-sidenav>
    <mat-sidenav-content class="m2app-dark">
      <div class="app-content">

<mat-card class="mappingDashboard">

  <!--
    ONE definition of the seven §4 state labels, used by the Status column AND
    by the Status filter below. Writing them out twice is exactly how a filter
    ends up offering a word the table never shows.

    Declared first so the `stateLabel` reference exists before either use.
  -->
  <ng-template #stateLabel let-state>
    <ng-container [ngSwitch]="state">
      <span *ngSwitchCase="'NOT_PLANNED'"       i18n="@@MappingStateNotPlanned">Not yet planned</span>
      <span *ngSwitchCase="'PLAN_STALE'"        i18n="@@MappingStatePlanStale">New items since the last plan</span>
      <span *ngSwitchCase="'READY_SIMPLE'"      i18n="@@MappingStateReadySimple">Ready, nothing to decide</span>
      <span *ngSwitchCase="'READY_PARTIAL'"     i18n="@@MappingStateReadyPartial">Ready, some decisions needed</span>
      <span *ngSwitchCase="'AWAITING_CUSTOMER'" i18n="@@MappingStateAwaitingCustomer">Waiting on your answers</span>
      <span *ngSwitchCase="'CUSTOMER_DECIDED'"  i18n="@@MappingStateCustomerDecided">Answered, ready to apply</span>
      <span *ngSwitchCase="'COMPLETE'"          i18n="@@MappingStateComplete">Complete</span>
      <span *ngSwitchDefault>{{ state }}</span>
    </ng-container>
  </ng-template>

  <!--
    Search and status filter.

    The search box matches the SAME three fields as the role picker's
    organization combo -- name, organization id, and the primary user's email
    address. An operator who has just been telephoned has exactly one of those
    three to hand and should not have to remember which of our screens accepts
    which.
  -->
  <div class="dashboardFilters floatContainer">
    <mat-form-field class="organizationSearch" appearance="outline">
      <mat-label i18n="@@Find an organization">Find an organization</mat-label>
      <input matInput type="text"
             [ngModel]="search" (ngModelChange)="searchChanged($event)"
             i18n-placeholder="@@searchRoleOrgSearch"
             placeholder="Search for organization name, organization Id, or primary organization user email">
      <button matSuffix mat-icon-button *ngIf="search" (click)="clearSearch()"
              i18n-title="@@Clear" title="Clear">
        <mat-icon>close</mat-icon>
      </button>
      <!--
        Said out loud. An operator who searches an email address and gets
        nothing must not conclude the customer is absent when the truth is
        that the addresses have not arrived yet.
      -->
      <mat-hint *ngIf="!adminEmailsLoaded" i18n="@@MappingEmailSearchUnavailable">Email addresses are still loading. Searching by name or Id works now.</mat-hint>
    </mat-form-field>

    <mat-form-field class="statusFilter" appearance="outline">
      <mat-label i18n="@@Status">Status</mat-label>
      <mat-select multiple
                  [ngModel]="selectedStates" (ngModelChange)="statesChanged($event)"
                  i18n-placeholder="@@All statuses" placeholder="All statuses">
        <mat-select-trigger>
          <ng-container *ngTemplateOutlet="stateLabel; context: { $implicit: selectedStates[0] }"></ng-container>
          <span class="moreStates" *ngIf="selectedStates.length > 1">+{{ (selectedStates.length - 1) | numberFormat }}</span>
        </mat-select-trigger>
        <mat-option *ngFor="let state of allStates" [value]="state">
          <ng-container *ngTemplateOutlet="stateLabel; context: { $implicit: state }"></ng-container>
        </mat-option>
      </mat-select>
    </mat-form-field>

    <!--
      Network only. A serviceProvider's rows are all its own, so the control
      would be a one-option select that cannot change anything.
    -->
    <mat-form-field class="serviceProviderFilter" appearance="outline" *ngIf="showServiceProvider">
      <mat-label i18n="@@Service Provider">Service Provider</mat-label>
      <mat-select [ngModel]="selectedServiceProviderId" (ngModelChange)="serviceProviderChanged($event)">
        <mat-option [value]="0" i18n="@@All service providers">All service providers</mat-option>
        <mat-option *ngFor="let serviceProvider of serviceProviderOptions" [value]="serviceProvider.id">
          <span *ngIf="serviceProvider.name">{{ serviceProvider.name }}</span>
          <span class="spId">{{ serviceProvider.id | numberFormat }}</span>
        </mat-option>
      </mat-select>
    </mat-form-field>

    <div class="dashboardHeaderActions">
      <button mat-stroked-button (click)="toggleOnlyNeedingAttention()"
              [class.filterActive]="onlyNeedingAttention"
              title="Show only organizations that still need something done"
              i18n-title="@@OnlyNeedingAttentionToolTip">
        <mat-icon>{{ onlyNeedingAttention ? 'filter_alt' : 'filter_alt_off' }}</mat-icon>
        <span i18n="@@Needs attention only">Needs attention only</span>
      </button>
      <button mat-stroked-button *ngIf="isFiltered" (click)="clearAllFilters()"
              title="Show every organization again" i18n-title="@@ClearFiltersToolTip">
        <mat-icon>filter_alt_off</mat-icon>
        <span i18n="@@Clear filters">Clear filters</span>
      </button>
      <button mat-stroked-button (click)="load()" [disabled]="loading || refreshing"
              title="Reload" i18n-title="@@RefreshToolTip">
        <mat-icon>refresh</mat-icon>
        <span i18n="@@Refresh">Refresh</span>
      </button>
    </div>
  </div>

  <!--
    The lede, because the table's whole job is this ratio: a handful of
    organizations worth a phone call, out of a few hundred that are not.
  -->
  <div class="dashboardSummary" *ngIf="!loading && !loadFailed">
    <span class="summaryValue">{{ totalNeedingDecision | numberFormat }}</span>
    <span i18n="@@MappingOrganizationsNeedingConversation">organizations have groupings that need a customer decision</span>
    <span class="summaryOf">
      <span i18n="@@of">of</span>
      <span class="summaryTotal">{{ totalOrganizations | numberFormat }}</span>
    </span>
    <!--
      Only when a filter is on. A count of what is NOT being shown is the one
      thing that stops a filtered table being read as the whole truth.
    -->
    <span class="summaryFiltered" *ngIf="isFiltered">
      <span i18n="@@Showing">Showing</span>
      <span class="summaryValue">{{ shownOrganizations | numberFormat }}</span>
    </span>
    <!--
      Inside the summary row, not above the table. A banner that appears and
      disappears pushes the table down a line and back up again, which is a
      smaller version of exactly the jump this is here to avoid.
    -->
    <span class="refreshNote" *ngIf="refreshing">
      <mat-spinner diameter="12"></mat-spinner>
      <span i18n="@@MappingUpdatingList">Updating the list</span>
    </span>
  </div>

  <!--
    A background refresh that failed. The rows below are still the rows we
    had, so they stay; what must not happen is the screen quietly presenting
    stale counts as current ones.
  -->
  <div class="refreshFailedNote" *ngIf="refreshFailed && !loading && !loadFailed">
    <mat-icon>sync_problem</mat-icon>
    <span i18n="@@MappingRefreshFailed">This list could not be updated, so the counts below may be out of date.</span>
    <button mat-stroked-button (click)="load()" [disabled]="loading || refreshing"
            title="Reload" i18n-title="@@RefreshToolTip">
      <mat-icon>refresh</mat-icon>
      <span i18n="@@Refresh">Refresh</span>
    </button>
  </div>

  <div class="loadingRow" *ngIf="loading">
    <mat-spinner diameter="24"></mat-spinner>
    <span i18n="@@Loading">Loading</span>
  </div>

  <!-- A failed load is never rendered as an empty, finished-looking table. -->
  <div class="errorPanel" *ngIf="loadFailed">
    <mat-icon color="warn">error_outline</mat-icon>
    <span i18n="@@MappingDashboardLoadFailed">The mapping dashboard could not be loaded, so no organization below is complete. Please try again.</span>
    <div class="errorDetail" *ngIf="loadErrorDetail">
      <span class="errorDetailLabel" i18n="@@Technical detail">Technical detail</span>
      <span class="errorDetailText">{{ loadErrorDetail }}</span>
    </div>
  </div>

  <div class="tableWrapper" *ngIf="!loading && !loadFailed">
    <table mat-table [dataSource]="dataSource" matSort matSortActive="needsDecision" matSortDirection="desc" class="mappingTable">

      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@Organization">Organization</th>
        <td mat-cell *matCellDef="let row">
          <div class="orgName">{{ row.name }}</div>
          <div class="orgId">{{ row.org_ID | numberFormat }}</div>
          <!--
            Shown only when the join found one. It is a search key first and a
            display field second, so it is never a fallback for the name: an
            organization with no admin user simply has no line here.
          -->
          <div class="orgAdminEmail" *ngIf="row.adminEmail"
               i18n-title="@@Primary organization user email" title="Primary organization user email">
            <mat-icon>alternate_email</mat-icon>{{ row.adminEmail }}
          </div>
        </td>
      </ng-container>

      <!--
        Rendered only for `network` -- `columns` omits it otherwise, so the
        definition below is simply never used rather than needing its own
        *ngIf. The id is always shown: names are not unique in practice and
        an operator quoting one to the server needs the number.
      -->
      <ng-container matColumnDef="serviceProvider">
        <th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@Service Provider">Service Provider</th>
        <td mat-cell *matCellDef="let row">
          <div class="spName" *ngIf="serviceProviderName(row.serviceProvider_ID)">{{ serviceProviderName(row.serviceProvider_ID) }}</div>
          <div class="spId">{{ row.serviceProvider_ID | numberFormat }}</div>
        </td>
      </ng-container>

      <ng-container matColumnDef="state">
        <th mat-header-cell *matHeaderCellDef i18n="@@Status">Status</th>
        <td mat-cell *matCellDef="let row">
          <!--
            A row mid-plan sends `state: "NOT_PLANNED"` with
            `run.phase: "PLANNING"`, because §4 has no state for a plan in
            progress and the server will not invent one outside the contract
            (server, 2026-10-24f §2). Correct on the wire, but rendering the
            state verbatim put "Not yet planned" beside this row's own spinner
            and disabled Plan button.

            So the PHASE is what this chip reads, which is exactly what the
            server asked us to gate on. No new §4 state is needed - the
            disagreement was only ever in the rendering.
          -->
          <span class="statusChip" [ngClass]="isPlanning(row) ? 'state-PLANNING' : 'state-' + row.state">
            <ng-container *ngIf="isPlanning(row)">
              <span i18n="@@MappingStateBeingPlanned">Being planned</span>
            </ng-container>
            <ng-container *ngIf="!isPlanning(row)">
              <ng-container *ngTemplateOutlet="stateLabel; context: { $implicit: row.state }"></ng-container>
            </ng-container>
          </span>
        </td>
      </ng-container>

      <!-- THE column. Everything else on the row is supporting detail. -->
      <ng-container matColumnDef="needsDecision">
        <th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@Needs a decision">Needs a decision</th>
        <td mat-cell *matCellDef="let row">
          <span class="decisionCount" [ngClass]="{ 'zero': isSimple(row) }">{{ needsDecision(row) | numberFormat }}</span>
          <span class="decisionHint" *ngIf="isSimple(row)" i18n="@@MappingNoConversationNeeded">no contact needed</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="proposed">
        <th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@Proposed groups">Proposed groups</th>
        <td mat-cell *matCellDef="let row">
          <span class="detailValue">{{ proposedCount(row) | numberFormat }}</span>
          <span class="detailNote" *ngIf="row.proposals && row.proposals.singleton">
            <span i18n="@@On their own">On their own</span>
            <span class="detailValue">{{ row.proposals.singleton | numberFormat }}</span>
          </span>
        </td>
      </ng-container>

      <ng-container matColumnDef="unmapped">
        <th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@Unmapped">Unmapped</th>
        <td mat-cell *matCellDef="let row">
          <span class="detailValue">{{ unmappedCount(row) | numberFormat }}</span>
          <span class="detailNote">
            <span i18n="@@Books">Books</span>
            <span class="detailValue">{{ row.unmappedBooks | numberFormat }}</span>
            <span i18n="@@Drafts">Drafts</span>
            <span class="detailValue">{{ row.unmappedDrafts | numberFormat }}</span>
          </span>
        </td>
      </ng-container>

      <ng-container matColumnDef="run">
        <th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@Planned">Planned</th>
        <td mat-cell *matCellDef="let row">
          <span *ngIf="row.run && row.run.planned" class="detailValue">{{ row.run.planned | date: 'short' }}</span>
          <!-- Not while a plan is being built: "Never" beside a spinner reads
               as though the click was ignored. -->
          <span *ngIf="!row.run && !isPlanning(row)" class="detailNote" i18n="@@Never">Never</span>
          <!--
            A spinner, not just a word. This is the only moving thing on the
            row, and it is what distinguishes "your click did something" from
            "your click did nothing".
          -->
          <span *ngIf="isPlanning(row)" class="detailNote workingNote">
            <mat-spinner diameter="12"></mat-spinner>
            <span i18n="@@Working">Working&hellip;</span>
          </span>
          <!--
            Said on the row, not only in the tooltip: an operator scanning the
            table should not have to hover to find out which organizations
            have had nothing done to them yet.
          -->
          <span *ngIf="!row.run && !isPlanning(row)" class="planPrompt" i18n="@@MappingSelectToPlan">Select to plan</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let row">
          <!--
            Both buttons are coloured by what the row needs, so the thing to
            press is findable without reading the status column first.
          -->
          <div class="rowActions">
            <button mat-stroked-button class="planButton" [ngClass]="planButtonClass(row)"
                    (click)="plan(row, $event)" [disabled]="isPlanning(row)"
                    title="Work out proposed groupings. This reads only - nothing is created."
                    i18n-title="@@PlanMappingToolTip">
              <mat-spinner *ngIf="isPlanning(row)" diameter="14"></mat-spinner>
              <span i18n="@@Plan">Plan</span>
            </button>
            <!--
              Hidden once the organization has nothing unmapped left. The
              proposal counters beside it are plan-time and never decay, so
              this used to be offered forever on a Complete row where it could
              only produce supersedes - see canApplySimple().
            -->
            <button mat-flat-button color="primary" class="applyButton"
                    [ngClass]="isSimple(row) ? 'applyClear' : 'applyDecisions'"
                    *ngIf="canApplySimple(row)"
                    [disabled]="isBusy(row)"
                    (click)="applySimple(row, $event)"
                    title="Create every Project that needs no decision"
                    i18n-title="@@ApplySimpleToolTip">
              <span i18n="@@MappingApplyUncontroversial">Create the clear ones</span>
            </button>
          </div>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="columns"></tr>
      <!--
        The row opens the SAME component the customer sees. Not a similar one:
        an operator on the phone has to be looking at what is being described.

        EVERY row opens, unconditionally. Gating the click on "this row has a
        plan" was tried and reverted on the same day: the dashboard payload
        reports `run: null` for every organization, so the gate was closed for
        all of them and the screen had no working row click at all. An
        organization with no plan now opens a page that says so and offers to
        plan it, which is the job of the page rather than of the gate.
      -->
      <tr mat-row *matRowDef="let row; columns: columns;"
          class="mappingRow"
          [ngClass]="rowClasses(row)"
          [title]="rowTitle(row)"
          (click)="selectRow(row)"></tr>
    </table>

    <!--
      Three different emptinesses, because they call for three different next
      actions: clear the search, stop hiding finished work, or go and ask why
      this operator has no organizations at all.
    -->
    <div class="emptyPanel" *ngIf="dataSource.data.length === 0">
      <mat-icon>{{ isFiltered ? 'search_off' : 'check_circle' }}</mat-icon>
      <span *ngIf="isSearchOrStatusFiltered" i18n="@@MappingNoOrganizationsMatchFilter">No organization matches the search and filters above.</span>
      <span *ngIf="!isSearchOrStatusFiltered && onlyNeedingAttention" i18n="@@MappingNoOrganizationsNeedAttention">No organization needs anything done right now.</span>
      <span *ngIf="!isFiltered" i18n="@@MappingNoOrganizationsInScope">There are no organizations in scope.</span>
      <button mat-stroked-button *ngIf="isFiltered" (click)="clearAllFilters()">
        <mat-icon>filter_alt_off</mat-icon>
        <span i18n="@@Clear filters">Clear filters</span>
      </button>
    </div>

    <mat-paginator [pageSizeOptions]="[25, 50, 100]" [pageSize]="25" showFirstLastButtons></mat-paginator>
  </div>
</mat-card>

      </div>
    </mat-sidenav-content>
  </mat-sidenav-container>
</div>
